import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'

const ACTION_KEYS = [
  'openInPhone',
  'removeFromSequence',
  'addToAnotherSequence',
  'assignToAgent',
  'createTasks',
  'export',
] as const

type SelectionAction = (typeof ACTION_KEYS)[number]

interface CampaignSelectionBarProps {
  count: number
  onClear: () => void
  /** Actions without a handler render disabled rather than as dead buttons. */
  handlers: Partial<Record<SelectionAction, () => void>>
  busy?: boolean
}

/**
 * The bulk bar over the contact list. It replaces nothing — it appears above
 * the filters when rows are ticked, so the counts stay visible while acting on
 * a subset of them.
 */
export function CampaignSelectionBar({
  count,
  onClear,
  handlers,
  busy = false,
}: CampaignSelectionBarProps) {
  const { t } = useTranslation()
  if (count === 0) return null
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-campaign-running-surface px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-3.5">
        <p className="text-sm font-medium text-gray-900">
          {t('campaigns.contact.selection.count', { count })}
        </p>
        <span
          aria-hidden="true"
          className="hidden h-4.5 w-px bg-campaign-bar-email sm:block"
        />
        <div className="flex flex-wrap items-center gap-2">
          {ACTION_KEYS.map((key) => {
            const handler = handlers[key]
            return (
              <button
                key={key}
                type="button"
                disabled={handler == null || busy}
                onClick={handler}
                className={cn(
                  'rounded-md border border-campaign-bar-email bg-white px-3 py-1.25 text-xs font-medium text-campaign-running',
                  handler == null || busy
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer hover:bg-avatar-blue-soft',
                )}
              >
                {t(`campaigns.contact.selection.actions.${key}`)}
              </button>
            )
          })}
        </div>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-900"
      >
        {t('campaigns.contact.selection.clear')}
      </button>
    </div>
  )
}
